import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Share2, MapPin, Cloud, Tv, Tent, Radio } from "lucide-react";
import { TurfTalk, AuthExpiredError } from "@/api/client";
import { TeamBadge, Skeleton, PlayerName } from "@/components/ui-kit";
import { CharacterShow } from "@/components/CharacterShow";
import { isLiveStatus, pollIntervalForGame } from "@/lib/gameStatus";
import { Sports } from "@/api/sports";
import { MyPick } from "@/components/MyPick";
import { RoomWithNews } from "@/components/RoomWithNews";
import { GameNewsStrip } from "@/components/GameNewsStrip";
import { useRoomHandoff } from "@/lib/useRoomHandoff";
import { CHAR_COLOR } from "@/lib/tokens";
import { formatSnapshotTime } from "@/lib/datetime";
import { trackGameView } from "@/lib/telemetry";

const TABS = ["Overview", "Team Stats", "Play-by-Play", "Game Pulse", "Odds", "Picks", "Room"];

function Scoreboard({ game }) {
  const { home, away, status } = game;
  const live = isLiveStatus(status);
  return (
    <div className="tt-panel-3 rounded-2xl p-5">
      <div className="flex items-center justify-between mb-4">
        <span className="text-[11px] tracking-widest text-white/40 font-display">{game.league} · WEEK {game.week}</span>
        {live ? <span className="inline-flex items-center gap-1.5 text-[#ff6b60] text-xs font-display"><span className="w-1.5 h-1.5 rounded-full bg-[#D92D20] tt-live-dot" /> LIVE {game.clock || ""}</span>
          : <span className="text-white/50 text-xs font-display">{status.toUpperCase()}</span>}
      </div>
      <div className="grid grid-cols-3 items-center gap-2">
        {[away, home].map((t, i) => (
          <React.Fragment key={i}>
            <div className={`flex items-center gap-3 ${i === 1 ? "order-3 flex-row-reverse text-right" : ""}`}>
              <TeamBadge team={t} size={52} />
              <div>
                <div className="text-white font-medium leading-tight">{t.name}</div>
                <div className="text-white/40 text-xs tabnums">{t.record ? `${t.record.w}-${t.record.l}` : ""}</div>
              </div>
            </div>
            {i === 0 && (
              <div className="order-2 text-center">
                <div className="font-display text-5xl text-white tabnums flex items-center justify-center gap-3">
                  <span>{away.score ?? "–"}</span><span className="text-white/20 text-2xl">:</span><span>{home.score ?? "–"}</span>
                </div>
              </div>
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="mt-4 pt-4 border-t border-[#2A3544] flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-white/45">
        {game.venue && <span className="inline-flex items-center gap-1"><MapPin size={12} /> {game.venue}</span>}
        {game.weather && <span className="inline-flex items-center gap-1"><Cloud size={12} /> {game.weather.temp_f}°F {game.weather.condition}</span>}
        {game.broadcast && <span className="inline-flex items-center gap-1"><Tv size={12} /> {game.broadcast}</span>}
      </div>
      {game.context && <p className="mt-3 text-sm text-white/60">{game.context}</p>}
    </div>
  );
}

function TeamStats({ id }) {
  const [res, setRes] = useState(null);
  useEffect(() => { Sports.gameBoxscore(id).then(setRes).catch(() => setRes({ state: "error" })); }, [id]);
  if (!res) return <Skeleton className="h-40" />;
  if (res.state !== "ok") {
    const t = res.state === "empty" ? "No team stats yet" : res.state === "feature_unavailable" ? "Box score not available yet" : "Couldn't load box score";
    return <div className="tt-panel rounded-xl p-8 text-center text-white/40 text-sm" data-testid={`team-stats-${res.state}`}>{t}.</div>;
  }
  const teams = res.teams || [];
  const away = teams[0] || "AWAY", home = teams[1] || "HOME";
  return (
    <div className="tt-panel rounded-xl overflow-hidden" data-testid="game-team-stats">
      <div className="grid grid-cols-3 px-4 py-2.5 text-[11px] font-display tracking-wide text-white/40 border-b border-[#2A3544]">
        <span className="text-left">{away}</span><span className="text-center">STAT</span><span className="text-right">{home}</span>
      </div>
      {res.rows.map((r, i) => (
        <div key={i} className="grid grid-cols-3 px-4 py-2 text-sm border-t border-[#2A3544] first:border-t-0">
          <span className="text-left text-white tabnums">{r.values[0] ?? "—"}</span>
          <span className="text-center text-white/50 capitalize">{r.label}</span>
          <span className="text-right text-white tabnums">{r.values[1] ?? "—"}</span>
        </div>
      ))}
      {Array.isArray(res.players) && res.players.length > 0 && (
        <div className="border-t border-[#2A3544]" data-testid="boxscore-players">
          <div className="px-4 py-2 text-[11px] font-display tracking-wide text-white/40">PLAYERS</div>
          {res.players.slice(0, 30).map((pl, i) => {
            const person = pl.player || pl;
            const pid = person.id || person.player_id || pl.player_id || null;
            const line = Object.entries(pl.stats || pl.stat_line || {}).filter(([, v]) => v !== null && v !== undefined && typeof v !== "object").slice(0, 3)
              .map(([k, v]) => `${String(k).replace(/_/g, " ")} ${v}`).join(" · ");
            return (
              <div key={pid || i} className="flex items-center justify-between gap-3 px-4 py-2 text-sm border-t border-[#2A3544]" data-testid={`boxscore-player-${pid || i}`}>
                <PlayerName player={person} id={pid} className="text-white truncate" testid={`boxscore-player-link-${pid || i}`} />
                <span className="text-[11px] text-white/45 tabnums shrink-0 truncate">{[pl.team || pl.team_abbr, line].filter(Boolean).join(" · ")}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function PlayByPlay({ id, live }) {
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  const [all, setAll] = useState([]);
  const LIMIT = 50;
  useEffect(() => {
    Sports.gamePlays(id, skip, LIMIT)
      .then((r) => { setData(r); setAll((prev) => (skip === 0 ? (r.plays || []) : [...prev, ...(r.plays || [])])); })
      .catch(() => setData({ state: "error", plays: [], total: 0 }));
  }, [id, skip]);
  // Live polling: refresh the first window every 45s while the game is active.
  useEffect(() => {
    if (!live) return;
    const iv = setInterval(() => {
      Sports.gamePlays(id, 0, LIMIT).then((r) => { setData(r); setAll(r.plays || []); }).catch(() => {});
    }, 45000);
    return () => clearInterval(iv);
  }, [id, live]);
  if (!data) return <Skeleton className="h-40" />;
  if (!all.length) {
    const t = data.state === "feature_unavailable" ? "Play-by-play not available yet" : data.state === "error" ? "Couldn't load play-by-play" : "No play-by-play available for this game yet";
    return <div className="tt-panel rounded-xl p-8 text-center text-white/40 text-sm" data-testid="pbp-empty">{t}.</div>;
  }
  const hasMore = (data.total || 0) > all.length;
  return (
    <div className="space-y-2" data-testid="game-play-by-play">
      {all.map((p, i) => (
        <div key={p.id ?? i} className="tt-panel rounded-lg px-3 py-2.5 flex items-start gap-3">
          <div className="text-[11px] text-white/40 tabnums shrink-0 w-14">{[p.period ? `Q${p.period}` : "", p.clock || ""].filter(Boolean).join(" ")}</div>
          <div className="text-sm text-white/85 flex-1">{p.text || "—"}</div>
          {(p.awayScore != null && p.homeScore != null) && <div className="text-[11px] text-white/40 tabnums shrink-0">{p.awayScore}-{p.homeScore}</div>}
        </div>
      ))}
      {hasMore && <button onClick={() => setSkip(all.length)} data-testid="pbp-load-more" className="w-full py-2 rounded-lg tt-panel text-white/70 text-sm hover:text-white transition-colors">Load more plays</button>}
    </div>
  );
}


function Pulse({ id, live }) {
  const [pulse, setPulse] = useState(null);
  useEffect(() => { TurfTalk.pulse(id).then(setPulse).catch(() => setPulse({ events: [] })); }, [id]);
  useEffect(() => {
    if (!live) return;
    const iv = setInterval(() => { TurfTalk.pulse(id).then(setPulse).catch(() => {}); }, 45000);
    return () => clearInterval(iv);
  }, [id, live]);
  const events = pulse?.events || pulse?.pulse || [];
  if (!pulse) return <Skeleton className="h-40" />;
  if (!events.length) return <div className="tt-panel rounded-xl p-8 text-center text-white/40 text-sm">No live plays yet for this game.</div>;
  return (
    <div className="tt-panel rounded-xl p-4">
      <div className="relative pl-5">
        <div className="absolute left-[7px] top-1 bottom-1 w-px bg-[#2A3544]" />
        {events.map((e, i) => (
          <div key={e.id || i} className="relative mb-4 last:mb-0">
            <span className="absolute -left-[13px] top-1 w-2.5 h-2.5 rounded-full bg-[#2F7D32] ring-4 ring-[#1A2332]" />
            <div className="text-sm text-white">{e.description || e.text || e.type}</div>
            <div className="text-[11px] text-white/40 tabnums">{e.clock || e.period || ""}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Odds({ id }) {
  const [odds, setOdds] = useState(null);
  useEffect(() => { Sports.gameOdds(id).then(setOdds).catch(() => setOdds({ state: "error", snapshots: [] })); }, [id]);
  const snaps = odds?.snapshots || [];
  if (!odds) return <Skeleton className="h-40" />;
  if (!snaps.length) {
    const t = odds.state === "feature_unavailable" ? "Odds not available yet" : odds.state === "error" ? "Couldn't load odds" : "No odds snapshots available";
    return <div className="tt-panel rounded-xl p-8 text-center text-white/40 text-sm" data-testid="odds-empty">{t}.</div>;
  }
  return (
    <div className="tt-panel rounded-xl overflow-hidden">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-white/40 text-xs tracking-wide border-b border-[#2A3544]">
          <th className="p-3 font-medium">SNAPSHOT</th><th className="p-3 font-medium">SPREAD</th><th className="p-3 font-medium">TOTAL</th><th className="p-3 font-medium">ML (H/A)</th>
        </tr></thead>
        <tbody className="tabnums">
          {snaps.map((s, i) => (
            <tr key={s.id || i} className="border-b border-[#2A3544] last:border-0">
              <td className="p-3 text-white/50" data-testid={`odds-snapshot-time-${i}`}>{formatSnapshotTime(s.captured_at ?? s.received_at ?? s.timestamp ?? s.last_updated)}</td>
              <td className="p-3 text-[#F4B740]">{s.spread > 0 ? `+${s.spread}` : s.spread}</td>
              <td className="p-3 text-white">{s.total}</td>
              <td className="p-3 text-white">{s.moneyline_home} / {s.moneyline_away}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Picks({ id, game }) {
  const [data, setData] = useState(null);
  useEffect(() => { TurfTalk.picks(id).then(setData).catch(() => setData({ characters: [] })); }, [id]);
  if (!data) return <Skeleton className="h-40" />;
  const chars = data.characters || [];
  return (
    <div className="space-y-4">
      <MyPick game={game} />
      {!data.active && <div className="text-xs text-white/40">{data.message || "Character picks are exhibition only — not betting advice."}</div>}
      <div className="grid sm:grid-cols-3 gap-4">
        {chars.map((c) => (
          <div key={c.id} className="tt-panel rounded-xl p-4" style={{ borderTop: `3px solid ${c.accent || CHAR_COLOR[c.id]}` }} data-testid={`pick-char-${c.id}`}>
            <img src={`/characters/${c.id}_master.png`} alt={c.name} className="h-24 mx-auto object-contain mb-2" draggable={false} />
            <div className="font-display text-lg text-white text-center">{c.name}</div>
            <div className="text-[11px] text-white/40 text-center tabnums">Record {c.record?.w}-{c.record?.l}</div>
            <p className="text-xs text-white/55 mt-2 text-center">{c.tagline}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function RoomTab({ gameId, league = null, refreshNonce = 0 }) {
  const [room, setRoom] = useState(null);
  const [err, setErr] = useState("");
  // Reset + guard so a game switch never shows the previous game's room.
  useEffect(() => {
    let active = true;
    setRoom(null); setErr("");
    TurfTalk.officialRoom(gameId).then((r) => { if (active) setRoom(r.room); }).catch((e) => { if (active) setErr(e.message); });
    return () => { active = false; };
  }, [gameId]);
  if (err) return <div className="tt-panel rounded-xl p-8 text-center text-white/40 text-sm">{err}</div>;
  if (!room) return <Skeleton className="h-[460px]" />;
  return (
    <RoomWithNews roomId={room.id} title={room.name} gameId={room.game_id || gameId} league={room.league || league} height={520} embedded refreshNonce={refreshNonce} />
  );
}

export default function GameDetailPage() {
  const { id } = useParams();
  const [game, setGame] = useState(null);
  const [tab, setTab] = useState("Overview");
  const [side, setSide] = useState({ tailgates: [], streams: [] });
  const [notFound, setNotFound] = useState(false);
  // "Debate this" hand-off — all share state is owned at game scope by the hook
  // (survives Room↔Overview tab remounts; no auto-resend; generation-scoped
  // completions). debateStory also opens the Room tab.
  const { storyStatus, roomRefresh, debateStory: handoffDebate } = useRoomHandoff(id);
  const debateStory = useCallback((item) => { handoffDebate(item); setTab("Room"); }, [handoffDebate]);

  const load = useCallback(() => {
    TurfTalk.game(id).then((r) => { setGame(r.game); trackGameView(id); }).catch((e) => { if (!(e instanceof AuthExpiredError)) setNotFound(true); });
    TurfTalk.tailgateList({ game_id: id }).then((r) => setSide((s) => ({ ...s, tailgates: r.tailgates || [] }))).catch(() => {});
    TurfTalk.liveList({ game_id: id }).then((r) => setSide((s) => ({ ...s, streams: r.livestreams || [] }))).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // Shared polling policy: fast (45s) while live or inside the active kickoff
  // window, slow (5m) while otherwise open, and STOP once the game is final.
  const live = isLiveStatus(game?.status);
  const gameIntervalMs = pollIntervalForGame(game);
  useEffect(() => {
    if (gameIntervalMs == null) return; // final → stop fast poll
    const iv = setInterval(() => {
      TurfTalk.game(id).then((r) => setGame(r.game)).catch(() => {});
    }, gameIntervalMs);
    return () => clearInterval(iv);
  }, [id, gameIntervalMs]);

  const share = () => { navigator.clipboard?.writeText(window.location.href); toast.success("Shareable game link copied"); };

  if (notFound) return <div className="max-w-6xl mx-auto px-8 py-20 text-center text-white/50">Game not found.</div>;
  if (!game) return <div className="max-w-6xl mx-auto px-8 py-8"><Skeleton className="h-48 mb-4" /><Skeleton className="h-96" /></div>;

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-6">
      <div className="flex items-center justify-between mb-4">
        <Link to="/games" className="inline-flex items-center gap-1.5 text-white/50 hover:text-white text-sm" data-testid="back-to-games"><ArrowLeft size={16} /> Games</Link>
        <button onClick={share} data-testid="share-game" className="inline-flex items-center gap-2 px-3 h-9 rounded-lg tt-panel text-white/80 hover:text-white text-sm transition-colors"><Share2 size={15} /> Share</button>
      </div>

      <div className="grid lg:grid-cols-[1fr_400px] gap-6 items-start">
        <div className="min-w-0">
          <Scoreboard game={game} />
          <GameNewsStrip gameId={id} league={game?.league || null} awayAbbr={game?.away?.abbr || game?.away?.abbreviation || null} homeAbbr={game?.home?.abbr || game?.home?.abbreviation || null} onDebate={debateStory} storyStatus={storyStatus} />
          <div className="flex gap-1 mt-5 mb-4 border-b border-[#2A3544] overflow-x-auto tt-scroll">
            {TABS.map((t) => (
              <button key={t} onClick={() => setTab(t)} data-testid={`tab-${t.replace(/\s/g, "-").toLowerCase()}`}
                className={`px-4 h-11 text-sm font-display tracking-wide whitespace-nowrap transition-colors border-b-2 -mb-px ${tab === t ? "border-[#2F7D32] text-white" : "border-transparent text-white/50 hover:text-white"}`}>
                {t}
              </button>
            ))}
          </div>
          <div data-testid={`tab-content-${tab.replace(/\s/g, "-").toLowerCase()}`}>
            {tab === "Overview" && (
              <div className="space-y-4">
                <div className="tt-panel rounded-xl p-5">
                  <div className="font-display tracking-widest text-xs text-white/40 mb-2">MATCHUP</div>
                  <p className="text-white/70 text-sm">{game.context || "Two teams, one broadcast desk. Follow the room, make your picks, and watch the Character Show pregame debate in the right rail."}</p>
                </div>
                <Pulse id={id} live={live} />
              </div>
            )}
            {tab === "Game Pulse" && <Pulse id={id} live={live} />}
            {tab === "Team Stats" && <TeamStats id={id} />}
            {tab === "Play-by-Play" && <PlayByPlay id={id} live={live} />}
            {tab === "Odds" && <Odds id={id} />}
            {tab === "Picks" && <Picks id={id} game={game} />}
            {tab === "Room" && <RoomTab gameId={id} league={game?.league || null} refreshNonce={roomRefresh} />}
          </div>
        </div>

        {/* Right rail */}
        <aside className="space-y-5 lg:sticky lg:top-6" data-testid="game-right-rail">
          <div>
            <div className="flex items-center gap-2 mb-2"><Tv size={15} className="text-[#4ea152]" /><span className="font-display tracking-wide text-white text-sm">CHARACTER SHOW</span></div>
            <CharacterShow gameId={id} game={game} />
          </div>

          <div className="tt-panel rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3"><Tent size={15} className="text-white/50" /><span className="font-display tracking-wide text-white text-sm">TAILGATES</span></div>
            {side.tailgates.length ? side.tailgates.map((t) => (
              <Link key={t.id} to={`/tailgates`} className="block py-2 border-b border-[#2A3544] last:border-0 text-sm text-white/80 hover:text-white">{t.title || t.name || "Tailgate"}</Link>
            )) : <p className="text-white/40 text-sm">No tailgates for this game yet.</p>}
          </div>

          <div className="tt-panel rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3"><Radio size={15} className="text-white/50" /><span className="font-display tracking-wide text-white text-sm">FAN STREAMS</span></div>
            {side.streams.length ? side.streams.map((s) => (
              <Link key={s.id} to={`/live`} className="block py-2 border-b border-[#2A3544] last:border-0 text-sm text-white/80 hover:text-white">{s.title || "Fan stream"}</Link>
            )) : <p className="text-white/40 text-sm">No fan streams for this game yet.</p>}
          </div>
        </aside>
      </div>
    </div>
  );
}
